import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { CheckInView } from "../../components/accommodation/check-in";
import { PageSkeleton } from "../../components/accommodation/page-skeleton";
import { useGamesTimeZone } from "../../components/travel-leg-form";
import { canSeeHostAccommodation, canUseDesk } from "../../lib/accommodation/access";
import { useDefaultGamesId } from "../../lib/competition/games";
import { isYmd, todayIn } from "../../lib/travel/view";

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);

/**
 * Check-in & out (her `#/acc/checkin`): the host desk. URL: `day` (games'
 * zone; absent = today), `mode=out` (absent = check-in), `building`.
 * A hotel's desk works from its own page (S5's hotel view).
 */
export const Route = createFileRoute("/_app/accommodation/check-in")({
	validateSearch: z.object({
		day: lenient(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
		mode: lenient(z.enum(["out"])),
		building: lenient(z.string().uuid()),
	}),
	beforeLoad: ({ context }) => {
		if (!canSeeHostAccommodation(context.session) || !canUseDesk(context.session)) throw redirect({ to: "/accommodation" });
	},
	component: CheckInPage,
});

function CheckInPage() {
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const gamesId = useDefaultGamesId(search.games_id);
	const timeZone = useGamesTimeZone(gamesId);
	if (!gamesId || !timeZone) return <PageSkeleton />;
	const today = todayIn(timeZone);
	return (
		<CheckInView
			gamesId={gamesId}
			timeZone={timeZone}
			today={today}
			date={isYmd(search.day) ? search.day : today}
			onDate={(d) => void navigate({ search: (s) => ({ ...s, day: d === today ? undefined : d }), replace: true })}
			mode={search.mode === "out" ? "out" : "in"}
			onMode={(m) => void navigate({ search: (s) => ({ ...s, mode: m === "out" ? ("out" as const) : undefined }), replace: true })}
			buildingId={search.building}
			onBuilding={(id) => void navigate({ search: (s) => ({ ...s, building: id }), replace: true })}
		/>
	);
}
